import { fullJitter, POLICIES } from '@one-deployment/shared'
import type { BoxTokenRequest, BoxTokenResponse } from '@one-deployment/api'
import { post } from './api'

export type BoxPurpose = 'terminal' | 'stream' | 'files' | 'chat'

/**
 * Mint a 60 s single-use token and open a socket straight to the box through
 * its tunnel (01 §3.4). The token rides in `Sec-WebSocket-Protocol`, never the
 * URL, so it does not land in tunnel logs. It authorises the upgrade only; a
 * reconnect mints a new one.
 */
export async function openBoxSocket(target: { workspaceId?: string; sessionId?: string; tmuxSession?: string; run?: boolean }, purpose: BoxPurpose, mode: 'ro' | 'rw'): Promise<WebSocket> {
  const body: BoxTokenRequest = { ...target, purpose, mode }
  const t = await post<BoxTokenResponse>('/api/boxes/token', body)
  const ws = new WebSocket(t.url, ['trisof.v1', `bearer.${t.token}`])
  ws.binaryType = 'arraybuffer'
  return ws
}

/**
 * A one-time link into the session's dev server on the box's preview host.
 * Opening it sets the host's cookie; the token in it works once, so mint a
 * fresh one for every load.
 */
export async function previewLink(sessionId: string): Promise<string> {
  const body: BoxTokenRequest = { sessionId, purpose: 'preview', mode: 'ro' }
  return (await post<BoxTokenResponse>('/api/boxes/token', body)).url
}

export type BoxSocketState = 'connecting' | 'open' | 'reconnecting' | 'unreachable' | 'ended'

/**
 * A box socket that reconnects with full jitter. After six failed attempts in a
 * row it reports `unreachable` and keeps trying slowly; the UI says which
 * server it cannot reach rather than spinning (03 §8).
 */
export class ReconnectingBoxSocket {
  private ws: WebSocket | null = null
  private attempt = 0
  private stopped = false
  private timer: ReturnType<typeof setTimeout> | null = null

  constructor(
    private readonly open: () => Promise<WebSocket>,
    private readonly handlers: {
      onOpen?: (ws: WebSocket) => void
      onMessage: (data: string | ArrayBuffer) => void
      onState: (s: BoxSocketState, attempt: number) => void
      /** Close codes the box uses for "this session is over": don't reconnect. */
      isFinal?: (code: number) => boolean
    },
  ) {}

  start(): void {
    this.stopped = false
    void this.connect()
  }

  send(data: string | ArrayBufferLike | Uint8Array): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(data as string)
  }

  get socket(): WebSocket | null {
    return this.ws
  }

  stop(): void {
    this.stopped = true
    if (this.timer) clearTimeout(this.timer)
    this.ws?.close(1000)
    this.ws = null
  }

  private async connect(): Promise<void> {
    if (this.stopped) return
    this.handlers.onState(this.attempt === 0 ? 'connecting' : this.attempt > 5 ? 'unreachable' : 'reconnecting', this.attempt)
    let ws: WebSocket
    try {
      ws = await this.open()
    } catch {
      this.retry()
      return
    }
    if (this.stopped) {
      ws.close(1000)
      return
    }
    this.ws = ws
    ws.onopen = () => {
      this.attempt = 0
      this.handlers.onState('open', 0)
      this.handlers.onOpen?.(ws)
    }
    ws.onmessage = (m) => this.handlers.onMessage(m.data as string | ArrayBuffer)
    ws.onclose = (ev) => {
      if (this.stopped) return
      if (this.handlers.isFinal?.(ev.code)) {
        this.handlers.onState('ended', 0)
        return
      }
      this.retry()
    }
  }

  private retry(): void {
    if (this.stopped) return
    this.attempt += 1
    this.handlers.onState(this.attempt > 5 ? 'unreachable' : 'reconnecting', this.attempt)
    this.timer = setTimeout(() => void this.connect(), Math.max(300, fullJitter(this.attempt, POLICIES.reconnect)))
  }
}
